// TÖMEGES BESOROLÁS — a kijelölt kiadás-sorok kategóriája és/vagy vonatkozási hónapja egy lépésben.
//
// MIÉRT KELL: a besorolás tipikusan KÖTEGES munka. A NAV-import egyszerre hoz le egy hónapnyi bizonylatot,
// és ezek jó része ugyanabba a kategóriába tartozik (tárhely, szoftver-licenc, iroda) — soronkénti
// legördülővel ez tucatnyi kattintás ugyanarra a döntésre.
//
// A KÉT MEZŐ KÜLÖN-KÜLÖN elhagyható, és ez a lényeg: a „ne változzon" NEM ugyanaz, mint a „töröld".
// Egy hónap-átsorolás nem sodorhatja el a már elvégzett kategorizálást, és fordítva. Ezért három állapot van
// mezőnként — ne változzon · kifejezett érték · kifejezett visszaállítás —, nem kettő.
//
// A GOMB TILTOTT, amíg mindkét mező „ne változzon": egy ilyen köteg száz sort írna felül változatlanul, ami
// száz fölösleges busz-írás és száz audit-sor. A szerver ugyanezt 422-vel zárja (a felület csak a tükör).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { monthOptions } from '@/components/expenses/expense-month-cell';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { formatMonthLabel } from '@/lib/month';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { employeeSearch } from '@/features/employees/search';
import { categoryLeafOptions } from '@/features/expenses/api';

import type { ExpenseBulkClassifyBody, ExpenseCategoryRow } from '@atlas/contracts';

/** „Ne változzon" — a mező kimarad a kérésből. */
const KEEP = '__keep__';
/** Kifejezett visszaállítás: kategória → besorolatlan · hónap → származtasd a bizonylatból. */
const CLEAR = '__clear__';

export interface BulkClassifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hány sor van kijelölve (a címben és a gombon). */
  count: number;
  categories: ExpenseCategoryRow[];
  locale: string;
  /** A jelenleg nézett hónap — a hónap-ablak közepe. */
  month: string;
  pending: boolean;
  onSubmit: (patch: Omit<ExpenseBulkClassifyBody, 'ids'>) => void;
}

export function BulkClassifyDialog({
  open,
  onOpenChange,
  count,
  categories,
  locale,
  month,
  pending,
  onSubmit,
}: BulkClassifyDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState(KEEP);
  const [target, setTarget] = useState(KEEP);
  const [employee, setEmployee] = useState(KEEP);

  /**
   * ÁLL-E a munkatárs-mező. Ha a köteg egy KONKRÉT kategóriába sorol, az dönt (bér típusú tétel-e); ha a
   * kategória „ne változzon", a sorok a SAJÁT kategóriájukat tartják — azok között lehet bér-tétel, ezért
   * a mező ilyenkor áll. A BESOROLATLANRA állítás viszont biztosan kivezet a bér-tételek közül.
   */
  const employeeAllowed =
    category === KEEP ||
    (category !== CLEAR && categories.find((c) => c.id === category)?.requiresEmployee === true);

  // A mező ELREJTÉSE nem elég: ha a kezelő előbb munkatársat választ, majd nem-bér kategóriát, a korábbi
  // választás némán kimenne a kérésben. A tiltással EGYÜTT vissza is állítjuk „ne változzon"-ra.
  const employeeValue = employeeAllowed ? employee : KEEP;
  const nothingToDo = category === KEEP && target === KEEP && employeeValue === KEEP;

  const submit = (): void => {
    onSubmit({
      // A HIÁNYZÓ kulcs jelentése „ne nyúlj hozzá" — ezért nem `undefined`-et küldünk, hanem kihagyjuk.
      ...(category === KEEP ? {} : { categoryId: category === CLEAR ? null : category }),
      ...(target === KEEP ? {} : { accountingMonth: target === CLEAR ? null : target }),
      ...(employeeValue === KEEP
        ? {}
        : { employeeIdentityId: employeeValue === CLEAR ? null : employeeValue }),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        // Záráskor alaphelyzet: a következő köteg ne örökölje az előző választását.
        if (!next) {
          setCategory(KEEP);
          setTarget(KEEP);
          setEmployee(KEEP);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('expenses.bulk.title', { count })}</DialogTitle>
          <DialogDescription>{t('expenses.bulk.description')}</DialogDescription>
        </DialogHeader>

        {/* A KÖZÖS `DialogBody` (`px-6 py-4`) — a `DialogContent` szándékosan `p-0`, a paddingot a szekciók
            hozzák. Egy sima `div` itt a panel széléig engedte a tartalmat. */}
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Text variant="secondary" as="label" className="block">
              {t('expenses.columns.category')}
            </Text>
            {/* UGYANAZ a keresős, CSAK LEVÉL-választó, mint a sor-cellában (`categoryLeafOptions`) — a köteg
                és az egy sor nem besorolhat máshová. A HÁROM állapot egyetlen mezőben: üres = VÁLTOZATLAN
                (a törlés-X ide állít vissza), a lista első eleme a BESOROLATLANRA állítás, minden más
                konkrét tétel. */}
            <EntityCombobox
              value={category === KEEP ? null : category}
              onChange={(next) => setCategory(next ?? KEEP)}
              options={[
                { id: CLEAR, label: t('expenses.uncategorized'), sublabel: '' },
                ...categoryLeafOptions(categories, locale),
              ]}
              placeholder={t('expenses.bulk.keep')}
              aria-label={t('expenses.columns.category')}
            />
          </div>

          {/* MUNKATÁRS — itt KÉT állapot van, nem három: üres = változatlan, konkrét személy = hozzárendelés.
              A kötegelt LEVÁLASZTÁS kimarad, mert a választó SZERVER-keresős, és az `EntityCombobox`
              szándékosan vagy `search`-öt, vagy helyi `options`-t fogad — a `CLEAR` sentinelt tehát nem
              lehetne a találatok közé tenni. Egyenként a sor-cellában törölhető (X).
              CSAK BÉR TÍPUSÚ TÉTELNÉL áll (l. `employeeAllowed`). */}
          {employeeAllowed && (
            <div className="space-y-1.5">
              <Text variant="secondary" as="label" className="block">
                {t('expenses.employee')}
              </Text>
              <EntityCombobox
                value={employee === KEEP ? null : employee}
                onChange={(next) => setEmployee(next ?? KEEP)}
                search={employeeSearch(locale)}
                placeholder={t('expenses.bulk.keep')}
                aria-label={t('expenses.employee')}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Text variant="secondary" as="label" className="block">
              {t('expenses.month.label')}
            </Text>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger aria-label={t('expenses.month.label')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={KEEP}>{t('expenses.bulk.keep')}</SelectItem>
                <SelectItem value={CLEAR}>{t('expenses.bulk.monthDerived')}</SelectItem>
                {/* A hónapok a SOR-CELLÁVAL azonos forrásból és azonos, NYELV SZERINTI alakban — a köteges
                    és az egyenkénti út ugyanazt a listát kínálja. */}
                {monthOptions(month).map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatMonthLabel(m, i18n.language)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={nothingToDo || pending}>
            {t('expenses.bulk.apply', { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
